import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/lib/queries";
import { logIncident } from "@/lib/incident-log";

const searchSchema = z.object({
  reason: z.string().optional(),
  suggestReset: z.string().optional(),
});

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact support — VISIKT" },
      {
        name: "description",
        content: "Get in touch with the VISIKT team. We'll help you resolve any issue.",
      },
      { property: "og:title", content: "Contact support — VISIKT" },
      {
        property: "og:description",
        content: "Get in touch with the VISIKT team. We'll help you resolve any issue.",
      },
      { property: "og:url", content: "/contact" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/contact" }],
  }),
  validateSearch: searchSchema,
  component: ContactPage,
});

function ContactPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [prefilledName, setPrefilledName] = useState(false);
  const [prefilledEmail, setPrefilledEmail] = useState(false);

  // Pre-populate from signed-in user's profile
  useEffect(() => {
    if (user?.email && !prefilledEmail && !form.email) {
      setForm((f) => ({ ...f, email: user.email ?? "" }));
      setPrefilledEmail(true);
    }
  }, [user, prefilledEmail, form.email]);

  useEffect(() => {
    if (profile && !prefilledName && !form.name) {
      const name =
        [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim() ||
        profile.display_name ||
        "";
      if (name) {
        setForm((f) => ({ ...f, name, phone: f.phone || profile.phone || "" }));
        setPrefilledName(true);
      }
    }
  }, [profile, prefilledName, form.name]);

  useEffect(() => {
    if (search.reason) {
      toast.error(t("register.errors.signupFailed"));
      setForm((f) => ({
        ...f,
        message: f.message || t("contact.autoIntro", { reason: search.reason }),
      }));
    }
  }, [search.reason, t]);

  async function handleReset() {
    if (!form.email.trim()) {
      toast.error(t("auth.invalidEmail"));
      return;
    }
    setResetting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("auth.resetSent"));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error(t("auth.invalidEmail"));
      return;
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      toast.error(t("contact.errors.description"));
      return;
    }
    setLoading(true);
    const trimmedName = form.name.trim();
    const parts = trimmedName.split(/\s+/);
    const firstName = parts.length > 1 ? parts.slice(0, -1).join(" ") : trimmedName;
    const lastName = parts.length > 1 ? parts[parts.length - 1] : null;
    const { error } = await supabase.from("support_requests").insert({
      first_name: firstName || null,
      last_name: lastName || null,
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      issue_description: form.message.trim(),
      status: "new",
      kind: search.reason ? "registration_failure" : "contact_form",
    });
    setLoading(false);
    if (error) {
      console.error("Contact submit failed:", error);
      toast.error(t("contact.errors.submitFailed"));
      logIncident({
        severity: "error",
        source: "business",
        event_type: "contact_submit_failed",
        message: error.message,
        metadata: {
          code: error.code,
          kind: search.reason ? "registration_failure" : "contact_form",
        },
      });
      return;
    }
    logIncident({
      severity: "info",
      source: "business",
      event_type: "contact_submitted",
      message: search.reason ? "Registration-failure contact submitted" : "Contact form submitted",
    });
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-5 pt-8 pb-8 max-w-md mx-auto w-full flex items-center justify-between gap-3">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 px-5 max-w-md mx-auto w-full">
        {submitted ? (
          <div className="animate-entry">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {t("contact.receivedEyebrow")}
            </p>
            <h1 className="text-3xl font-display font-bold tracking-tight">
              {t("contact.receivedHeadline")}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">{t("contact.receivedBody")}</p>
            <div className="mt-6 flex gap-3">
              <Link to="/" className="py-3 px-4 rounded-md border border-border text-sm">
                {t("contact.backHome")}
              </Link>
              <Link to="/auth" className="py-3 px-4 rounded-md bg-accent text-primary text-sm">
                {t("auth.submitSignIn")}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8 animate-entry">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {t("contact.eyebrow")}
              </p>
              <h1 className="text-3xl font-display font-bold tracking-tight">
                {t("contact.headline")}
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">{t("contact.subtitle")}</p>
            </div>

            {search.suggestReset === "1" && (
              <div className="mb-5 rounded-md border border-border p-4 bg-muted/30">
                <p className="text-sm mb-2">{t("contact.resetSuggestion")}</p>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={resetting}
                  className="text-xs font-medium underline underline-offset-4 disabled:opacity-50"
                >
                  {resetting ? t("auth.waiting") : t("contact.resetAction")}
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3" noValidate>
              <Field label={t("contact.name")}>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={160}
                  autoComplete="name"
                  className="input-base"
                />
              </Field>
              <Field label={t("auth.email")}>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  maxLength={255}
                  autoComplete="email"
                  className="input-base"
                />
              </Field>
              <Field label={t("contact.phone")}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  maxLength={32}
                  autoComplete="tel"
                  className="input-base"
                />
              </Field>
              <Field label={t("contact.message")}>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  maxLength={4000}
                  className="input-base"
                  placeholder={t("contact.describePlaceholder")}
                />
              </Field>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50 transition-opacity"
              >
                {loading ? t("auth.waiting") : t("contact.submit")}
              </button>
            </form>
          </>
        )}
      </main>
      <div className="px-5 max-w-md mx-auto w-full mt-10">
        <PublicFooter />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
